
import SearchIcon from '@mui/icons-material/Search';
import { useCallback, useEffect, useState } from "react";
import { fetchData } from "../../../services/SubjectiveServices/chiefcomplaintformService";
import { format } from 'date-fns';
import { TableCell, TableRow } from "@mui/material";
import SearchBar from "../../../components/FormFilelds/SearchBar";
import TableComponent from "../../../components/Table/TableComponent";
import TablePaginationComponent from "../../../components/TablePaginationComponent";
import IconButton, { ClearButton } from '../../../components/Buttons/SubjectiveIcons';
import { Chiefcomplaint } from '../../../components/interface/SubjectiveInterfaces/ChiefComplaintInterface';
import SnackbarMessage from '../../../components/SnackBAr/SnackbarNotification';
import LoadingAnimation from '../../../components/LoadingAnimation';


interface ChiefComplaintsFormProps {
    patientId: string;
}

const ComplaintForm: React.FC<ChiefComplaintsFormProps> = ({ patientId }) => {
    const [chiefComplaints, setChiefComplaints] = useState<Chiefcomplaint[]>([]);
    const [totalChiefComplaints, setTotalChiefComplaints] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'info' | 'warning' | 'error'>('success');


    const [showClearFilterButton, setShowClearFilterButton] = useState(false);

    const showSnackbar = (message: string, severity: 'success' | 'info' | 'warning' | 'error') => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const fetchChiefComplaintsData = useCallback(async () => {
        setLoading(true);
        setError('');
    
        try {
            await new Promise((resolve) => setTimeout(resolve, 300));
    
            const params: Record<string, any> = {
                limit: rowsPerPage,
                page: page + 1,
                order_by: 'created_at',
                dir: 2,
                user_id: patientId,
                slug: 'chief-complaints',
                searchkey: searchQuery,
            };
    
           
            const data = await fetchData(params);
    
            const fetchedData = data.data.docs.map(item => {
                const additionInfo = item.addition_info || {};
                const dateTimeString = additionInfo.date || 'N/A';
                const date = dateTimeString !== 'N/A' ? new Date(dateTimeString) : null;
                return {
                    id: item.id,
                    date: date ? format(date, 'dd-MM-yyyy') : '',
                    time: date ? `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}` : '',
                    title: additionInfo.title || '',
                    notes: additionInfo.notes || '',
                };
            });
    
            setChiefComplaints(fetchedData);
            setTotalChiefComplaints(data.data.pagination.total || 0);
            setShowClearFilterButton(searchQuery.trim() !== '');
        } catch (error) {
            setError('Failed to fetch complaints.');
            console.error('Error fetching complaints', error);
            showSnackbar('Failed to fetch complaints.', 'error');
        } finally {
            setLoading(false);
        }
    }, [searchQuery, page, rowsPerPage, patientId]);
    



    useEffect(() => {
        fetchChiefComplaintsData();
    }, [fetchChiefComplaintsData]);


    const handleSearch = () => {
        if (searchTerm.trim() !== '') {
            setSearchQuery(searchTerm);
        } else {
            showSnackbar('Please enter a search term.', 'warning');
        }
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setSearchQuery('');
        setPage(0);
        fetchChiefComplaintsData();
    };

   

    const renderRow = (item: Chiefcomplaint, index: number) => {
        const date = item.date  ||'-';
       
        
        const time = item.time || '-';
        const title = item.title || '-';
        const notes = item.notes || '-';

        return (
            <TableRow key={index}>
                <TableCell className="px-4 py-3 font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }} > {page * rowsPerPage + index + 1}</TableCell>
                <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{`${date} - ${time}`}</TableCell>
                <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{title}</TableCell>
                <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{notes}</TableCell>
                
            </TableRow>
        );
    };

    return (
        <div>
            <div className="flex items-end justify-between w-full">
                <div className="flex flex-grow space-x-4">
                    <SearchBar
                        id={'Search_complaint'}
                        placeholder={'Search complaint'}
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                    <IconButton
                        onClick={handleSearch}
                        disabled={!searchTerm.trim()}
                        icon={<SearchIcon />}
                    />
                    {showClearFilterButton && (
                        <ClearButton onClick={handleClearFilters} />
                    )}
                </div>
                
            </div>
            
            {loading && <LoadingAnimation />} {/* Show spinner when loading */}
            {loading ? (
                <div className="text-center my-4">Loading...</div>
            ) : (
                <div>
                    <TableComponent
                        headers={['Date & Time', 'Complaints', 'Notes', 'Actions']}
                        data={chiefComplaints}
                        loading={loading}
                        error={error}
                        renderRow={renderRow}
                    />
                  
                </div>
            )}
            <TablePaginationComponent
                count={totalChiefComplaints}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(_event, newPage) => {
                    setPage(newPage);
                }}
                onRowsPerPageChange={(event) => {
                    const newRowsPerPage = parseInt(event.target.value, 10);
                    setRowsPerPage(newRowsPerPage);
                    setPage(0);
                }}
            />
            
            {/* Snackbar Component */}
            <SnackbarMessage
                open={snackbarOpen}
                message={snackbarMessage}
                severity={snackbarSeverity}
                onClose={() => setSnackbarOpen(false)}
            />
        </div>
    );
};

export default ComplaintForm;
